import { z } from 'zod';

// ---------------------------------------------------------------------------
// Staff compliance training (courses → modules → final quiz).
// Course content is platform-global (like standards); progress is per
// (org, user, course). See docs/CONTRACTS.md §Training.
// ---------------------------------------------------------------------------

export const CourseStatusSchema = z.enum(['active', 'archived']);
export type CourseStatus = z.infer<typeof CourseStatusSchema>;

export const CourseSchema = z.object({
  id: z.string(),
  code: z.string(),
  title: z.string(),
  description: z.string(),
  durationMinutes: z.number().int(),
  sortOrder: z.number().int(),
  status: CourseStatusSchema,
  moduleCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Course = z.infer<typeof CourseSchema>;

/** Module content as served to learners (no quiz answers here). */
export const CourseModuleSchema = z.object({
  id: z.string(),
  courseId: z.string(),
  sortOrder: z.number().int(),
  title: z.string(),
  contentMd: z.string(),
});
export type CourseModule = z.infer<typeof CourseModuleSchema>;

/** Quiz question as presented to a learner — never includes the answer. */
export const QuizQuestionPublicSchema = z.object({
  id: z.string(),
  moduleId: z.string(),
  text: z.string(),
  options: z.array(z.string()),
});
export type QuizQuestionPublic = z.infer<typeof QuizQuestionPublicSchema>;

export const CourseProgressStatusSchema = z.enum(['not_started', 'in_progress', 'completed']);
export type CourseProgressStatus = z.infer<typeof CourseProgressStatusSchema>;

/** Provenance of an attested (admin-recorded) completion, carried on progress. */
export const CourseAttestationSummarySchema = z.object({
  source: z.string(),
  attestedBy: z.string().nullable(),
  attestedByName: z.string().nullable(),
  attestedAt: z.string(),
});
export type CourseAttestationSummary = z.infer<typeof CourseAttestationSummarySchema>;

/**
 * Per-user progress on one course. `steps` = modules + the final quiz;
 * `completedSteps` counts completed modules (+1 once the quiz is passed).
 * A completed course whose `expiresAt` has passed reports `expired: true`
 * and should be retaken (the final quiz re-run refreshes it).
 * `attested` marks a completion recorded by an admin from a prior platform
 * rather than earned by the final quiz; `attestation` carries its provenance.
 */
export const CourseProgressSchema = z.object({
  courseId: z.string(),
  status: CourseProgressStatusSchema,
  completedModuleIds: z.array(z.string()),
  completedSteps: z.number().int(),
  steps: z.number().int(),
  percent: z.number().int(),
  completedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  expired: z.boolean(),
  attested: z.boolean(),
  attestation: CourseAttestationSummarySchema.nullable(),
});
export type CourseProgress = z.infer<typeof CourseProgressSchema>;

export const CourseListItemSchema = CourseSchema.extend({
  progress: CourseProgressSchema,
});
export type CourseListItem = z.infer<typeof CourseListItemSchema>;

export const CourseDetailSchema = z.object({
  course: CourseSchema,
  modules: z.array(CourseModuleSchema),
  progress: CourseProgressSchema,
  /** Size of the final-quiz question bank across all modules. */
  questionBankSize: z.number().int(),
  latestAttempt: z
    .object({
      id: z.string(),
      passed: z.boolean().nullable(),
      score: z.number().int().nullable(),
      total: z.number().int(),
      submittedAt: z.string().nullable(),
    })
    .nullable(),
});
export type CourseDetail = z.infer<typeof CourseDetailSchema>;

/** An open (unsubmitted) final-quiz attempt with its drawn questions. */
export const QuizAttemptSchema = z.object({
  id: z.string(),
  courseId: z.string(),
  questions: z.array(QuizQuestionPublicSchema),
  createdAt: z.string(),
});
export type QuizAttempt = z.infer<typeof QuizAttemptSchema>;

export const QuizAnswerInputSchema = z.object({
  answers: z
    .array(
      z.object({
        questionId: z.string(),
        selectedIndex: z.number().int().min(0),
      }),
    )
    .min(1),
});
export type QuizAnswerInput = z.infer<typeof QuizAnswerInputSchema>;

export const QuizResultSchema = z.object({
  attemptId: z.string(),
  passed: z.boolean(),
  score: z.number().int(),
  total: z.number().int(),
  results: z.array(
    z.object({
      questionId: z.string(),
      correct: z.boolean(),
      correctIndex: z.number().int(),
      explanation: z.string(),
    }),
  ),
  progress: CourseProgressSchema,
});
export type QuizResult = z.infer<typeof QuizResultSchema>;

/** Org-wide rollup for the Track Training page (admin + reviewer). */
export const TrainingMemberProgressSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
  courses: z.array(CourseProgressSchema),
});
export type TrainingMemberProgress = z.infer<typeof TrainingMemberProgressSchema>;

export const TrainingOrgProgressSchema = z.object({
  courses: z.array(CourseSchema),
  members: z.array(TrainingMemberProgressSchema),
});
export type TrainingOrgProgress = z.infer<typeof TrainingOrgProgressSchema>;

// ---------- Attestations (recorded prior completions) ----------

/**
 * Admin-recorded completion of a course on a previous platform. Exactly one
 * of userId/email (the email may belong to a pending invitation, in which
 * case the record is linked when that person joins) and exactly one of
 * courseId/courseCode. `completedAt` is a calendar date, never in the future.
 */
export const CreateTrainingAttestationInputSchema = z
  .object({
    userId: z.string().min(1).optional(),
    email: z.string().email().optional(),
    courseId: z.string().min(1).optional(),
    courseCode: z.string().min(1).optional(),
    completedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Provenance, e.g. the previous training platform. */
    source: z.string().min(1).max(500),
    /** Revoke an existing live attestation for the same person + course first. */
    replace: z.boolean().default(false),
  })
  .refine((v) => (v.userId ? 1 : 0) + (v.email ? 1 : 0) === 1, {
    message: 'Provide exactly one of userId or email',
    path: ['userId'],
  })
  .refine((v) => (v.courseId ? 1 : 0) + (v.courseCode ? 1 : 0) === 1, {
    message: 'Provide exactly one of courseId or courseCode',
    path: ['courseId'],
  });
export type CreateTrainingAttestationInput = z.infer<typeof CreateTrainingAttestationInputSchema>;

export const TrainingAttestationSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  /** Null until the invited email joins the organisation. */
  userId: z.string().nullable(),
  userName: z.string().nullable(),
  email: z.string(),
  courseId: z.string(),
  courseCode: z.string(),
  courseTitle: z.string(),
  /** Attested completion date (YYYY-MM-DD). */
  completedAt: z.string(),
  expiresAt: z.string(),
  source: z.string(),
  attestedBy: z.string().nullable(),
  attestedByName: z.string().nullable(),
  attestedAt: z.string(),
  revokedAt: z.string().nullable(),
});
export type TrainingAttestation = z.infer<typeof TrainingAttestationSchema>;

export const TrainingAttestationListSchema = z.object({
  items: z.array(TrainingAttestationSchema),
});
export type TrainingAttestationList = z.infer<typeof TrainingAttestationListSchema>;

// ---------- Import bundle (seed data + import endpoint) ----------

export const ImportQuizQuestionSchema = z.object({
  text: z.string().min(1),
  /** 2–6 answer options; exactly one is correct. */
  options: z.array(z.string().min(1)).min(2).max(6),
  correctIndex: z.number().int().min(0),
  explanation: z.string().default(''),
});
export type ImportQuizQuestion = z.infer<typeof ImportQuizQuestionSchema>;

export const ImportCourseModuleSchema = z.object({
  title: z.string().min(1),
  /** Markdown body shown to the learner. */
  content: z.string().min(1),
  /** Question bank contributed to the final quiz (≥1 per module). */
  questions: z.array(ImportQuizQuestionSchema).min(1),
});
export type ImportCourseModule = z.infer<typeof ImportCourseModuleSchema>;

export const CourseImportBundleSchema = z.object({
  course: z.object({
    code: z
      .string()
      .min(2)
      .regex(/^[a-z0-9][a-z0-9-]*$/),
    title: z.string().min(1),
    description: z.string().default(''),
    durationMinutes: z.number().int().min(1).default(15),
    sortOrder: z.number().int().default(0),
  }),
  modules: z.array(ImportCourseModuleSchema).min(1),
});
export type CourseImportBundle = z.infer<typeof CourseImportBundleSchema>;
